import { z } from 'zod';
import {
  MAX_MESSAGES,
  MAX_TEXT_LEN,
  MessageSchema,
  MetaSchema,
  Rfc3339DateTime,
} from './create-chat.js';

/**
 * `POST /api/v1/workstream/chat` — a chat turn inside an **existing**
 * workstream. Distinct from `/workstream/create/chat` (ADR-0024, frozen —
 * creates nothing, no `workstreamId`): this endpoint is scoped to a real
 * workstream and also carries the Librarian "invite agent" hint
 * (`agentName` → `AgentInvokeRequest.agentName`).
 *
 * Stateless like the create-flow pair (no router path param — the router
 * only does exact-string method+path matching, see `handlers/router.ts` —
 * so `workstreamId` travels in the body, same as `AgentInvokeRequest`):
 * `messages` is the client-held transcript, resent each call. No
 * server-side persistence yet — `WorkstreamState` lives in-memory in the
 * Python pipeline, persisted in DynamoDB (see ADR-0030 / ADR-0034).
 */
export const WorkstreamChatRequestSchema = z.strictObject({
  workstreamId: z.string().min(1),
  /**
   * Every workstream has at least one focus (product-archaeology §2.8 —
   * `Workstream (1) -> Focus (many) -> Message (many)`). No focus CRUD or
   * persistence yet — the SPA sends the constant `'default'` implicit focus
   * until multi-focus switching lands; the field exists now so the contract
   * doesn't need a breaking change when it does.
   */
  focusId: z.string().min(1),
  messages: z.array(MessageSchema).max(MAX_MESSAGES),
  question: z.string().min(1).max(MAX_TEXT_LEN),
  questionCreatedAt: Rfc3339DateTime.optional(),
  /** Invite-agent hint — forwarded as-is to `AgentInvokeRequest.agentName`. */
  agentName: z.string().optional(),
  /**
   * Turn kind — forwarded to `AgentInvokeRequest.action`. `exitDiscovery`
   * transitions the workstream from discovery mode back to normal routing;
   * absent means a plain chat turn.
   */
  action: z.enum(['chat', 'exitDiscovery']).optional(),
});
export type WorkstreamChatRequest = z.infer<typeof WorkstreamChatRequestSchema>;

/** The Librarian's routing mode for a workstream (see `AgentInvokeRequest.action`). */
export const WorkstreamModeSchema = z.enum(['discovery', 'invite', 'normal']);
export type WorkstreamMode = z.infer<typeof WorkstreamModeSchema>;

/** The consensus tracker's verdict for a turn (Python `ConsensusStatus`). */
export const WorkstreamConsensusSchema = z.enum(['agreed', 'conflicted', 'incomplete']);
export type WorkstreamConsensus = z.infer<typeof WorkstreamConsensusSchema>;

/**
 * Same shape as the create-flow chat turn: echoed user turn + assistant reply.
 * `mode` is additive-optional (ADR-0024 §7): the Librarian's current routing
 * mode for the workstream, when the agent reports it — the SPA uses it to
 * gate the invite buttons / show the exit-discovery affordance.
 */
export const WorkstreamChatDataSchema = z.strictObject({
  userMessage: MessageSchema,
  reply: MessageSchema,
  mode: WorkstreamModeSchema.optional(),
  /**
   * Consensus verdict for this turn + the tracker's conflict detail, when the
   * agent reports them (additive-optional, same posture as `mode`) — the SPA
   * surfaces a conflict banner (plan flow step 8b).
   */
  consensus: WorkstreamConsensusSchema.optional(),
  conflictDetail: z.string().optional(),
});
export type WorkstreamChatData = z.infer<typeof WorkstreamChatDataSchema>;

/** `{ data, meta }` envelope (tolerant-out, ADR-0024 §11 convention). */
export const WorkstreamChatResponseSchema = z.looseObject({
  data: WorkstreamChatDataSchema,
  meta: MetaSchema,
});
export type WorkstreamChatResponse = z.infer<typeof WorkstreamChatResponseSchema>;
